import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { post_api } from '../../../../../redux/api_funcs';
import { get_api_url } from '../../../../../utils/urls';

class AddIncentivePopup extends Component {
  state = {
    incentiveAmount: '',
    incentiveType: '',
    incentiveReason: '',
    errors: {},
    submitting: false,
    submitError: ''
  };

  dialogRef = createRef();

  firstInputRef = createRef();

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    // Focus the first input on mount
    if (this.firstInputRef.current) {
      this.firstInputRef.current.focus();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (e) => {
    if (e.key === 'Escape' && !this.state.submitting) {
      this.props.onClose();
    }
  };

  handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !this.state.submitting) {
      this.props.onClose();
    }
  };

  validate = () => {
    const { incentiveAmount, incentiveType, incentiveReason } = this.state;
    const errors = {};

    // Validate amount
    const amount = parseInt(incentiveAmount, 10);
    if (!incentiveAmount || incentiveAmount.trim() === '') {
      errors.incentiveAmount = 'Amount is required';
    } else if (Number.isNaN(amount) || amount <= 0) {
      errors.incentiveAmount = 'Amount must be greater than 0';
    } else if (!Number.isInteger(amount)) {
      errors.incentiveAmount = 'Amount must be a whole number';
    }

    // Validate type
    if (!incentiveType || incentiveType.trim() === '') {
      errors.incentiveType = 'Type is required';
    } else if (incentiveType.trim().length < 3) {
      errors.incentiveType = 'Type must be at least 3 characters';
    } else if (incentiveType.trim().length > 100) {
      errors.incentiveType = 'Type must not exceed 100 characters';
    }

    // Validate reason
    if (!incentiveReason || incentiveReason.trim() === '') {
      errors.incentiveReason = 'Reason is required';
    } else if (incentiveReason.trim().length < 5) {
      errors.incentiveReason = 'Reason must be at least 5 characters';
    } else if (incentiveReason.trim().length > 200) {
      errors.incentiveReason = 'Reason must not exceed 200 characters';
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = (e) => {
    e.preventDefault();

    if (!this.validate()) return;

    const { sessionUuid, onClose, onSuccess } = this.props;
    const { incentiveAmount, incentiveType, incentiveReason } = this.state;

    this.setState({ submitting: true, submitError: '' });

    const url = get_api_url(
      '/v2/cms_user/teacher-payments/sessions/add_payout_incentive',
      null,
      true
    );
    const payload = {
      session_uuid: sessionUuid,
      incentive_amount: parseInt(incentiveAmount, 10),
      incentive_type: incentiveType.trim(),
      incentive_reason: incentiveReason.trim()
    };

    post_api(url, payload, true)
      .then(() => {
        this.setState({ submitting: false });
        if (onSuccess) onSuccess();
        onClose();
      })
      .catch((error) => {
        console.error('Error adding incentive:', error);
        this.setState({
          submitting: false,
          submitError: error.response?.data?.message || 'Failed to add incentive. Please try again.'
        });
      });
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      [name]: value,
      errors: { ...prevState.errors, [name]: '' }
    }));
  };

  render() {
    const { onClose } = this.props;
    const { incentiveAmount, incentiveType, incentiveReason, errors, submitting, submitError } =
      this.state;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={this.handleBackdropClick}
        role="presentation"
      >
        <div
          ref={this.dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-incentive-title"
          className="bg-white rounded-lg shadow-xl w-full max-w-md focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 id="add-incentive-title" className="text-lg font-semibold text-gray-900">
              Add Incentive
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={submitting}
              aria-label="Close dialog"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={this.handleSubmit} className="p-4" noValidate>
            {submitError && (
              <div
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
                role="alert"
              >
                {submitError}
              </div>
            )}

            <div className="mb-4">
              <label
                htmlFor="incentiveAmount"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Incentive Amount{' '}
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
                <span className="sr-only">(required)</span>
              </label>
              <input
                ref={this.firstInputRef}
                type="number"
                id="incentiveAmount"
                name="incentiveAmount"
                value={incentiveAmount}
                onChange={this.handleInputChange}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.incentiveAmount ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter amount"
                min="1"
                step="1"
                disabled={submitting}
                aria-invalid={!!errors.incentiveAmount}
                aria-describedby={errors.incentiveAmount ? 'incentiveAmount-error' : undefined}
              />
              {errors.incentiveAmount && (
                <p id="incentiveAmount-error" className="mt-1 text-sm text-red-600" role="alert">
                  {errors.incentiveAmount}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label
                htmlFor="incentiveType"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Incentive Type{' '}
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
                <span className="sr-only">(required)</span>
                <span className="text-gray-400 font-normal ml-1">(3-100 characters)</span>
              </label>
              <input
                type="text"
                id="incentiveType"
                name="incentiveType"
                value={incentiveType}
                onChange={this.handleInputChange}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.incentiveType ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Early completion, Extra effort"
                maxLength={100}
                disabled={submitting}
                aria-invalid={!!errors.incentiveType}
                aria-describedby="incentiveType-count incentiveType-error"
              />
              <div className="flex justify-between mt-1">
                {errors.incentiveType ? (
                  <p id="incentiveType-error" className="text-sm text-red-600" role="alert">
                    {errors.incentiveType}
                  </p>
                ) : (
                  <span />
                )}
                <span id="incentiveType-count" className="text-xs text-gray-400" aria-live="polite">
                  {incentiveType.length}/100
                </span>
              </div>
            </div>

            <div className="mb-4">
              <label
                htmlFor="incentiveReason"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reason{' '}
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
                <span className="sr-only">(required)</span>
                <span className="text-gray-400 font-normal ml-1">(5-200 characters)</span>
              </label>
              <textarea
                id="incentiveReason"
                name="incentiveReason"
                value={incentiveReason}
                onChange={this.handleInputChange}
                className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
                  errors.incentiveReason ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter reason for incentive"
                rows={3}
                maxLength={200}
                disabled={submitting}
                aria-invalid={!!errors.incentiveReason}
                aria-describedby="incentiveReason-count incentiveReason-error"
              />
              <div className="flex justify-between mt-1">
                {errors.incentiveReason ? (
                  <p id="incentiveReason-error" className="text-sm text-red-600" role="alert">
                    {errors.incentiveReason}
                  </p>
                ) : (
                  <span />
                )}
                <span
                  id="incentiveReason-count"
                  className="text-xs text-gray-400"
                  aria-live="polite"
                >
                  {incentiveReason.length}/200
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Adding...
                  </span>
                ) : (
                  'Add Incentive'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

AddIncentivePopup.propTypes = {
  sessionUuid: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

AddIncentivePopup.defaultProps = {
  onSuccess: null
};

export default AddIncentivePopup;
